import React, { useMemo, useEffect } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  ReactFlowProvider,
  useReactFlow
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { groupBy } from 'lodash'
import { AssignedBadge } from 'shared/badges'
import { useBusinessRules } from 'api/hooks/use-business-rules'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane, faCheckCircle } from '@fortawesome/free-solid-svg-icons'

const ProcessFlowchartTab = ({ processId, selectedDepartment, selectedApprovalTypes, amount }) => {
  const params = {
    process_type_id: processId,
    perPage: 1000,
    orderBy: 'step_number',
    orderDirection: 'asc'
  }

  const { data, isSuccess } = useBusinessRules({
    params
  })

  const { nodes, edges } = useMemo(() => {
    if (!isSuccess) return { nodes: [], edges: [] }

    let rules = data.data.filter((r) => r.step_number !== null)

    // Filter by department
    // If "All" is selected, show only rules with null/empty department (catch-all rules)
    // If a specific department is selected, show rules for that department + catch-all rules
    if (selectedDepartment) {
      rules = rules.filter(
        (r) => !r.department || r.department === selectedDepartment
      )
    }

    // Filter by approval types if provided
    if (selectedApprovalTypes && selectedApprovalTypes.length > 0) {
      rules = rules.filter((r) => selectedApprovalTypes.includes(r.approval_type))
    }

    // Filter by amount if provided
    if (amount !== '') {
      const amountNum = parseFloat(amount)
      if (!isNaN(amountNum)) {
        rules = rules.filter(
          (r) =>
            (r.lower_amount === null || r.lower_amount <= amountNum) &&
            (r.upper_amount === null || r.upper_amount >= amountNum)
        )
      }
    }

    const grouped = groupBy(rules, 'step_number')
    const steps = Object.keys(grouped)
      .map((n) => parseInt(n, 10))
      .sort((a, b) => a - b)

    // spacing between the steps vertically and nodes horizontally
    const stepSpacing = 150
    const horizontalSpacing = 250

    const nodes = steps.flatMap((step, stepIndex) =>
      grouped[step].map((r, index) => ({
        id: r.id.toString(),
        data: {
          label: (
            <div className="text-center">
              <div className="fw-bold">Rule #: {r.external_id}</div>
              <div className="fw-bold">
                <AssignedBadge rule={r} />
              </div>
              <div className="text-muted small">Step {r.step_number}</div>
              <div className="text-muted small">
                Department: {r.department ? r.department : 'All'}
              </div>
              <div className="text-muted small">
                Amount: {r.lower_amount} - {r.upper_amount}
              </div>
              <div className="text-muted small">
                {r.rule_type === 'A' ? (
                  <div className="text-muted small">
                    <FontAwesomeIcon icon={faCheckCircle} /> Approval:{' '}
                    {r.approval_type}
                  </div>
                ) : (
                  <div className="text-muted small">
                    <FontAwesomeIcon icon={faPaperPlane} /> Notification{' '}
                    {r.approval_type}
                  </div>
                )}
              </div>
            </div>
          )
        },
        position: { x: index * horizontalSpacing, y: stepIndex * stepSpacing }
      }))
    )

    const edges = []
    steps.forEach((step, stepIndex) => {
      if (stepIndex === steps.length - 1) return
      const nextStep = steps[stepIndex + 1]
      grouped[step].forEach((current) => {
        grouped[nextStep].forEach((next) => {
          edges.push({
            id: `${current.id}-${next.id}`,
            source: current.id.toString(),
            target: next.id.toString()
          })
        })
      })
    })

    return { nodes, edges }
  }, [data, isSuccess, selectedDepartment, selectedApprovalTypes, amount])

  if (!isSuccess) {
    return <div>Loading...</div>
  }

  return (
    <div style={{ width: '100%', height: 600 }}>
      <ReactFlowProvider>
        <FlowContainer nodes={nodes} edges={edges} />
      </ReactFlowProvider>
    </div>
  )
}

export default ProcessFlowchartTab

function FlowContainer({ nodes, edges }) {
  const { fitView } = useReactFlow()

  useEffect(() => {
    if (nodes.length > 0) {
      setTimeout(() => fitView(), 100)
    }
  }, [fitView, nodes])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      fitView
      minZoom={0.1}
      maxZoom={2}
      defaultViewport={{ x: 0, y: 0, zoom: 1 }}
    >
      <Background />
      <Controls />
    </ReactFlow>
  )
}
